import { ScheduleKind } from '../../types';
import { ScheduleOption } from './ScheduleTypeSelect';

/**
 * What each campaign kind offers in its "Schedule:" dropdown.
 *
 * These lists are the other half of a policy's schedule contract: a type whose
 * default `scheduledType` is missing here leaves the user with a value they can
 * neither see nor pick again. `scheduleOptionParity` pins the two together.
 */

interface OptionContext {
  /** 1-based; the first message has nothing to be relative to. */
  position: number;
  /** Enrollment only: this message opens the follow-up section. */
  isFirstFollowUp?: boolean;
  /** Enrollment section restored from the loaded/configured message. */
  enrollmentSection?: 'join' | 'scheduled';
}

const reminderOptions = ({ position }: OptionContext): ScheduleOption[] => [
  { value: 'contact_added', label: 'Contact Added to Calendar Event', disabled: position > 1 },
  { value: 'delay', label: 'Reminder' },
  { value: 'reply', label: 'After Confirmation', disabled: position === 1 },
];

// Position 1 has nothing to be relative to, so the relative options are omitted
// rather than greyed out. The flyer shares this list: its first message is the
// flyer itself, which can only go out immediately or at a set time.
const genericOptions = ({ position }: OptionContext): ScheduleOption[] => [
  { value: 'immediate', label: 'Immediately' },
  ...(position > 1 ? [{ value: 'delay', label: 'After Delay' }] : []),
  { value: 'time', label: 'Specific Date/Time' },
  ...(position > 1 ? [{ value: 'reply', label: 'After Response' }] : []),
];

const joinOptions = (isEnrollment: boolean) =>
  ({ position, isFirstFollowUp = false, enrollmentSection }: OptionContext): ScheduleOption[] => {
    // Anything above the follow-up boundary is still a join message, and a join
    // message runs inside the window: it cannot pick a wall-clock time.
    const isJoinMessage = isEnrollment && !isFirstFollowUp && enrollmentSection !== 'scheduled';
    const isFirstJoinMessage = isJoinMessage && position === 1;

    return [
      { value: 'immediate', label: 'Immediately', disabled: isFirstFollowUp },
      {
        value: 'delay',
        label: 'After Delay',
        disabled: isEnrollment ? isFirstJoinMessage || isFirstFollowUp : position === 1,
      },
      // Only enrollment offers an absolute time, and only for its follow-ups.
      ...(isEnrollment
        ? [{ value: 'time', label: 'Specific Date/Time', disabled: isJoinMessage }]
        : []),
      {
        value: 'reply',
        label: 'After Response',
        disabled: isEnrollment ? isJoinMessage || isFirstFollowUp : position === 1,
      },
    ];
  };

const BY_KIND: Record<ScheduleKind, ((context: OptionContext) => ScheduleOption[]) | null> = {
  reminder: reminderOptions,
  generic: genericOptions,
  flyer: genericOptions,
  join: joinOptions(false),
  enrollment: joinOptions(true),
  // Annual draws its own widget, and a recurring campaign keeps its schedule in
  // the campaign settings — neither has a dropdown.
  annual: null,
  recurring: null,
};

export const scheduleOptionsFor = (
  kind: ScheduleKind,
  context: OptionContext,
): ScheduleOption[] => BY_KIND[kind]?.(context) ?? [];

/** Kinds that render a "Schedule:" dropdown at all. */
export const hasScheduleOptions = (kind: ScheduleKind): boolean => BY_KIND[kind] !== null;
